/**
 * ログイン直後の画面（ホーム）の日本語（en.home.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。表記の決め事は ja.ts の冒頭と同じ。
 * 「機能」「モジュール」「有効化」をこのファイルに書かない（wording.test.ts が止める・en.home.ts の冒頭の WHY）
 */
import type { enHome } from './en.home';
import type { PluralMsg } from './index';

type HomeMessages = { [K in keyof typeof enHome]: (typeof enHome)[K] extends string ? string : string | PluralMsg };

export const jaHome: HomeMessages = {
  'home.eyebrow': 'あなたのバックエンド',
  'home.title': '公開・データ・お金は、どのプロジェクトにも用意してあります。',
  'home.lead': 'AIエージェントに作りたいものを頼むと、この3つを使ってプロジェクトを作ります。',
  'home.titleWithProjects': 'あなたのプロジェクト',

  'home.pillarsLabel': 'プロジェクトにもう付いているもの',
  'home.ready': '用意済み',
  'home.pillar.publish.title': '公開',
  'home.pillar.publish.body': 'プロジェクトを置く場所と、独自ドメイン。',
  'home.pillar.data.title': 'データ',
  'home.pillar.data.body': 'データベースと、自分で書くサーバーのプログラム（Functions）。テーブルもSQLも自由です。',
  'home.pillar.money.title': 'お金',
  'home.pillar.money.body': '課金の受け取りと、売上の出金。',
  'home.pillar.money.bodyLaunch': '前払いの残高と、プロジェクトが使った分の利用料。',

  'home.stepsTitle': 'はじめの一歩',
  'home.nextTitle': '次の一歩',
  'home.stepDone': '済み',
  'home.step.verify.title': 'メールアドレスを確認する',
  'home.step.verify.body': '{email}に届いたリンクを開いてください。確認が済むと、AIエージェントをつなげます。',
  'home.step.verify.bodyInvite': '{email}に届いたリンクを開いてください。確認すると{amount}の招待クレジットが入り（入金は不要です）、AIエージェントをつなげます。',
  'home.step.connect.title': 'AIをつなぐ',
  // どの AI にも貼る 1 つの文の上下（2026-10-11 console/one-connect・en と同じ）。文そのものは apps/site/src/connectPrompt.mjs
  'home.step.connect.copyLabel': 'これをコピーして、あなたのAIに貼ってください',
  // 2026-10-18 gtm/early-fixes（WHY は en.home.ts）
  'home.step.connect.anyAi': 'Claude Code、Codex、Cursor、Web の Claude・ChatGPT で使えます（パソコンのブラウザで）。',
  'home.step.connect.startPage': '初めての人へ: AI を使っている場所ごとの手順',
  'home.step.connect.noPrompt': '{product}のドキュメントの「AIエージェントから使う（MCP）」か「Web の Claude・ChatGPT で使う」をご覧ください。',
  'home.step.topUp.title': '残高を入れる',
  'home.step.topUp.body': '使った分だけ、この残高から原価で引きます。残高があると、AIエージェントがプロジェクトを作って公開できるようになります。',
  'home.step.topUp.cta': '入金する',
  'home.step.talk.title': '作りたいものをAIエージェントに頼む',
  'home.step.talk.body': '自分の言葉で大丈夫です。たとえば:',
  'home.example.1': '予約を受け付けるアプリを作って、公開して',
  'home.example.2': 'ログイン付きで、データベースに保存する共有の ToDo リストを作って',
  'home.example.3': '放置系のクリッカーのゲームを作って、セーブを付けて公開して',
  // 4 つ目 = オンライン対戦（MASTER-PLAN §0 founder 2026-10-02・en.home.ts の同じ所の WHY）
  'home.example.4': '友だちとオンラインで対戦できるクイズのゲームを作って、クイックマッチを付けて公開して',
  'home.step.publish.title': '公開する',
  'home.step.publish.body': '「公開して」と頼めば、プロジェクトにURLが付きます。独自ドメインはあとから付けられます。公開したプロジェクトはここに並びます。',

  'home.newProject': '新しいプロジェクト',
  'home.createHere': 'この画面からプロジェクトを作る',
  'home.projectsEmpty': 'まだプロジェクトはありません。AIエージェントが作ったプロジェクトはここに並びます。',
  'home.b.title': '何を作る？',
  'home.b.placeholder': '予約を受け付けるアプリ',
  'home.b.examplesLabel': 'たとえば:',
  'home.askExample.1': '予約を受け付けるアプリ',
  'home.askExample.2': 'ログイン付きの共有の ToDo リスト',
  'home.askExample.3': '放置系のクリッカー（ゲーム）',
  // 4 つ目 = オンライン対戦（MASTER-PLAN §0 founder 2026-10-02・en.home.ts の同じ所の WHY）
  'home.askExample.4': '友だちとオンラインで対戦できるクイズ（ゲーム・クイックマッチ付き）',
  // 最初の 1 つの頼み文（en.home.ts の同じ所の WHY・つなぐ文の最後の頼みとそろえる）
  'home.askExample.first': '{product}で小さなゲームを作って公開して。遊べるURLを教えて。',
  // 広告から来た人の最初の頼み（2026-10-18 gtm/early-fixes・WHY は en.home.ts）
  'home.askExample.firstAd': 'このゲームを{product}で公開して。遊べるURLを送って。',
  'home.askExample.noGameYet': 'まだゲームが無い？なら「{product}で小さなゲームを作って公開して」と頼んでください。',
  'home.askTemplate': '{product}で『{what}』を作って。ログインとデータベースと公開は{product}で。',
  'home.b.askLabel': 'AIエージェントに貼ってください',
  'home.b.copy': 'コピー',
  'home.b.readyLine': '公開・データ・お金は用意済み',
  'home.b.connectLabel': 'AIエージェントを一度だけつなぐ',
  'home.b.connected': 'AIエージェントとつながっています',
  'home.b.setupLabel': '先に済ませること',


  'home.c.title': 'プロジェクト',
  'home.c.empty': 'まだプロジェクトがありません。AIをつないで作るか、ここから作れます。',
  'home.c.stepsLabel': '手順',
  'home.c.connectBody': 'つなぐと、使っているAIが{product}の上でプロジェクトを作り、変え、公開できるようになります。',
  'home.c.askTitle': 'AIエージェントに頼む',
  'home.c.askBody': '例をコピーしてエージェントに貼ると、エージェントが{product}の上でプロジェクトを作り、公開まで進めます。',
  'home.c.examplesLabel': '頼み方の例',
  'home.c.create': 'プロジェクトを作る',
  'home.c.setupTitle': '残っている準備',
  // 2026-10-14 gtm/onboarding（en.home.ts の同じ所の WHY）
  'home.c.verifyWaiting': 'リンクを開くと、この画面は自動で次に進みます。',
  'home.c.connectWaiting': 'AIがつながるのを待っています。つながると、この画面が自動で変わります。',
  'home.c.connected': 'つながりました',
  'home.c.connectedCli': 'AIはコマンドでログインしました。{product}の道具も使えるようにするなら、AIを再起動して、聞かれたら{product}を許可してください。',
  'home.c.firstAskLabel': 'まずはこれを頼む',
  'home.c.firstAskNote': 'まずテスト版に置かれ、試せるリンクが届きます。みんなに公開する前に、一度だけ確認が出ます。',
  'home.c.moreExamplesLabel': 'ほかのものを頼むなら',
  // 最初のテスト版のカード（en.home.ts の同じ所の WHY）
  'home.c.firstTest.title': 'テスト版ができました',
  'home.c.firstTest.play': '開いてみる',
  'home.c.firstTest.next': 'よければ、AIにこう言ってください:',
  'home.c.firstTest.ask': 'みんなが開けるように公開して',
  'home.c.firstTest.note': 'みんなに公開する前に、一度だけ確認が出ます。'
};
